/**
 * CampaignPlannerPage/index.tsx — Orchestrator
 *
 * Admin-only: admin@dkafka.com sees the full planner.
 * All other users (or unauthenticated) see a "coming soon" card.
 *
 * Step machine:
 *   brief → arc-review → lock-progress → dashboard
 *
 * This component owns ALL state and ALL axios calls.
 * Step components receive only the props they need.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import "../../landing.css";

import StepBrief        from "./StepBrief";
import StepArcReview    from "./StepArcReview";
import StepLockProgress from "./StepLockProgress";
import StepDashboard    from "./StepDashboard";

import type { CarouselTemplateMeta } from "../DistillPageV2/types";
import { pageStyle, navStyle, mainStyle, card, ACCENT } from "../DistillPageV2/sharedStyles";
import type {
  Campaign, PlannerStep, CreateCampaignBody, ArcSlot, CampaignStatus, CampaignSummary,
} from "./types";

// ── Helper: pick the right step for an existing campaign by status ─────────
function stepForStatus(s: CampaignStatus): PlannerStep {
  switch (s) {
    case 'draft':
    case 'arc_locked':                       return 'arc-review';
    case 'generating':
    case 'rendering':                        return 'lock-progress';
    case 'locked':
    case 'active':
    case 'paused':
    case 'completed':
    case 'cancelled':                        return 'dashboard';
    default:                                 return 'dashboard';
  }
}

// ── Config ────────────────────────────────────────────────────────────────────

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8085";

const ADMIN_EMAILS = [
  "admin@dkafka.com",
  "vikas@dkafka.com",
  "aditi@dkafka.com",
  "shubham@dkafka.com",
];

// Dev spacing: 1-minute gaps so the full 7-post cycle runs in 7 minutes.
// Set to 0 (omit) for production scheduling.
const DEV_SPACING_MINUTES = process.env.REACT_APP_CAMPAIGN_DEV_SPACING ?? "1";

// ── Auth helpers ──────────────────────────────────────────────────────────────

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("distill_auth_token") || localStorage.getItem("distill_guest_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function axiosCfg() {
  return { withCredentials: true, headers: getAuthHeaders() };
}

function getAdminEmail(): string | null {
  const token = localStorage.getItem("distill_auth_token");
  if (!token) return null;
  try {
    const parts   = token.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1]));
    return (payload.email as string | undefined) ?? null;
  } catch { return null; }
}

function isAdmin(): boolean {
  const email = getAdminEmail();
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
}

// ── Coming-soon card ─────────────────────────────────────────────────────────

const ComingSoon: React.FC = () => (
  <div style={{ ...mainStyle, alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
    <div style={{
      ...card,
      maxWidth:   "400px",
      textAlign:  "center",
      padding:    "40px 32px",
    }}>
      <div style={{ fontSize: "36px", marginBottom: "16px" }}>🚀</div>
      <div style={{ fontSize: "20px", fontWeight: 800, marginBottom: "8px", letterSpacing: "-0.03em" }}>
        Campaign Planner
      </div>
      <div style={{ fontSize: "14px", color: "#888", lineHeight: "1.6", marginBottom: "20px" }}>
        LinkedIn campaign scheduling is coming soon.
        Sign in as an admin to access this feature.
      </div>
      <a
        href="/distill-v2"
        style={{
          display:        "inline-flex",
          alignItems:     "center",
          gap:            "6px",
          textDecoration: "none",
          color:          "#fff",
          background:     ACCENT,
          borderRadius:   "8px",
          padding:        "10px 18px",
          fontSize:       "14px",
          fontWeight:     700,
        }}
      >
        <span style={{ fontSize: "16px", lineHeight: 1 }}>←</span>
        Back to App
      </a>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const CampaignPlannerPage: React.FC = () => {
  const location = useLocation();
  const queryId  = new URLSearchParams(location.search).get('id');

  const [step,        setStep]        = useState<PlannerStep>("brief");
  const [campaign,    setCampaign]    = useState<Campaign | null>(null);

  // Templates (for StepBrief's template picker)
  const [templates,       setTemplates]       = useState<CarouselTemplateMeta[]>([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  // Other campaigns currently running — surfaced in StepBrief so the user
  // can avoid duplicating an in-flight campaign before they even start.
  const [runningCampaigns, setRunningCampaigns] = useState<CampaignSummary[]>([]);

  // Brief step
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const [submitError,   setSubmitError]   = useState<string | null>(null);

  // Arc step
  const [isSavingArc,  setIsSavingArc]  = useState(false);
  const [arcSaveError, setArcSaveError] = useState<string | null>(null);

  // Lock step
  const [isLocking,  setIsLocking]  = useState(false);
  const [lockError,  setLockError]  = useState<string | null>(null);

  // ── Fetch templates on mount ────────────────────────────────────────────
  useEffect(() => {
    if (!isAdmin()) return;
    axios.get(`${API_URL}/api/carousel/templates`, axiosCfg())
      .then(r => {
        setTemplates(r.data?.templates ?? []);
        setTemplatesLoaded(true);
      })
      .catch(() => setTemplatesLoaded(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch list of other campaigns (for awareness banner in brief step) ─
  useEffect(() => {
    if (!isAdmin()) return;
    axios.get<{ campaigns: CampaignSummary[] }>(`${API_URL}/api/campaigns`, axiosCfg())
      .then(r => {
        const list = (r.data.campaigns ?? []).filter(
          c => c.status === 'active' || c.status === 'paused' || c.status === 'generating' || c.status === 'rendering'
        );
        setRunningCampaigns(list);
      })
      .catch(() => { /* non-fatal: just hide the banner */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Deep-link: load a specific campaign when /campaign-planner?id=X ────
  useEffect(() => {
    if (!isAdmin()) return;
    if (!queryId) return;
    if (campaign?.id === Number(queryId)) return; // already loaded

    axios.get<Campaign>(`${API_URL}/api/campaigns/${queryId}`, axiosCfg())
      .then(r => {
        setCampaign(r.data);
        setStep(stepForStatus(r.data.status));
      })
      .catch(() => { /* unknown id — leave user on the brief step */ });
  }, [queryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── API helpers ─────────────────────────────────────────────────────────

  const fetchCampaign = useCallback(async (): Promise<Campaign> => {
    if (!campaign) throw new Error("No campaign");
    const r = await axios.get<Campaign>(`${API_URL}/api/campaigns/${campaign.id}`, axiosCfg());
    return r.data;
  }, [campaign]);

  // ── Step 0: create campaign ─────────────────────────────────────────────

  const handleCreateCampaign = async (body: CreateCampaignBody) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const r = await axios.post(`${API_URL}/api/campaigns`, body, axiosCfg());
      const data = r.data;
      // Build a Campaign object from the create response (posts are empty at this point)
      setCampaign({
        id:              data.id,
        name:            data.name,
        topic:           body.topic,
        goal:            body.goal,
        status:          data.status,
        corePrinciple:   data.corePrinciple,
        storyArc:        data.storyArc,
        arcTemplate:     data.arcTemplate ?? null,
        totalDays:       body.totalDays,
        dailyTime:       body.dailyTime,
        timezone:        body.timezone,
        schedulePattern: body.schedulePattern,
        scheduleDays:    body.scheduleDays ?? null,
        templateIds:     body.templateIds,
        colorTheme:      body.colorTheme,
        posts:           [],
      });
      setStep("arc-review");
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.error ?? "Failed to generate arc — please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step 1: save arc ────────────────────────────────────────────────────

  const handleSaveArc = async (arc: ArcSlot[]) => {
    if (!campaign) return;
    setIsSavingArc(true);
    setArcSaveError(null);
    try {
      await axios.patch(
        `${API_URL}/api/campaigns/${campaign.id}/arc`,
        { arc },
        axiosCfg()
      );
      setCampaign(prev => prev ? { ...prev, storyArc: arc } : prev);
    } catch (err: any) {
      setArcSaveError(
        err?.response?.data?.error ?? "Failed to save arc — please try again."
      );
    } finally {
      setIsSavingArc(false);
    }
  };

  // ── Step 1b: arc regenerated (after evaluator feedback) ───────────────────

  const handleArcRegenerated = (arc: ArcSlot[], newCorePrinciple: string) => {
    setCampaign(prev => prev ? { ...prev, storyArc: arc, corePrinciple: newCorePrinciple } : prev);
  };

  // ── Step 2: lock ────────────────────────────────────────────────────────

  const handleLock = async () => {
    if (!campaign) return;
    setIsLocking(true);
    setLockError(null);
    try {
      const qs = DEV_SPACING_MINUTES ? `?dev_spacing_minutes=${DEV_SPACING_MINUTES}` : "";
      await axios.post(
        `${API_URL}/api/campaigns/${campaign.id}/lock${qs}`,
        {},
        axiosCfg()
      );
    } catch (err: any) {
      setLockError(
        err?.response?.data?.error ?? "Failed to lock campaign — please try again."
      );
      setIsLocking(false);
      throw err;   // re-throw so StepLockProgress doesn't start polling
    }
    // Don't setIsLocking(false) here — StepLockProgress controls its own state
  };

  const handleLockComplete = (updated: Campaign) => {
    setCampaign(updated);
    setIsLocking(false);
    setStep("dashboard");
  };

  // ── Dashboard action handlers ───────────────────────────────────────────

  const handlePause = async () => {
    if (!campaign) return;
    await axios.post(`${API_URL}/api/campaigns/${campaign.id}/pause`, {}, axiosCfg());
  };

  const handleResume = async () => {
    if (!campaign) return;
    await axios.post(`${API_URL}/api/campaigns/${campaign.id}/resume`, {}, axiosCfg());
  };

  const handleSkipPost = async (pid: number) => {
    if (!campaign) return;
    await axios.post(
      `${API_URL}/api/campaigns/${campaign.id}/posts/${pid}/skip`,
      {}, axiosCfg()
    );
  };

  const handleReschedulePost = async (pid: number, scheduledAt: string) => {
    if (!campaign) return;
    await axios.patch(
      `${API_URL}/api/campaigns/${campaign.id}/posts/${pid}`,
      { scheduledAt },
      axiosCfg()
    );
  };

  const handleDelete = async () => {
    if (!campaign) return;
    await axios.delete(`${API_URL}/api/campaigns/${campaign.id}`, axiosCfg());
  };

  const handleReset = () => {
    setCampaign(null);
    setStep("brief");
    setSubmitError(null);
    setArcSaveError(null);
    setLockError(null);
    setIsLocking(false);
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (!isAdmin()) {
    return (
      <div style={pageStyle}>
        <nav style={navStyle}>
          <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: "18px" }}>
            distill
            <sup style={{ fontSize: "8px", letterSpacing: "0.1em", fontWeight: 600, color: ACCENT, verticalAlign: "super", marginLeft: "2px", textTransform: "uppercase" }}>
              v2
            </sup>
          </div>
        </nav>
        <ComingSoon />
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* ── Nav ──────────────────────────────────────────────────────────── */}
      <nav style={navStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Dashboard — primary navigation; user's home base */}
          <Link
            to="/dashboard"
            data-testid="cp-dashboard-link"
            title="Your Dashboard — all campaigns at a glance"
            style={{
              display:        "inline-flex",
              alignItems:     "center",
              gap:            "6px",
              textDecoration: "none",
              color:          "#fff",
              background:     ACCENT,
              border:         `1.5px solid ${ACCENT}`,
              borderRadius:   "8px",
              padding:        "6px 12px",
              fontSize:       "13px",
              fontWeight:     700,
              fontFamily:     "inherit",
            }}
          >
            <span style={{ fontSize: "13px", lineHeight: 1 }}>▦</span>
            Dashboard
          </Link>

          {/* Back to App — secondary */}
          <Link
            to="/distill-v2"
            data-testid="cp-back-to-app"
            title="Back to distill v2"
            style={{
              display:        "inline-flex",
              alignItems:     "center",
              gap:            "6px",
              textDecoration: "none",
              color:          "#444",
              background:     "#fff",
              border:         `1.5px solid #E4E4E0`,
              borderRadius:   "8px",
              padding:        "6px 12px",
              fontSize:       "13px",
              fontWeight:     700,
              fontFamily:     "inherit",
            }}
          >
            <span style={{ fontSize: "15px", lineHeight: 1 }}>←</span>
            App
          </Link>

          <span style={{ color: "#E4E4E0", fontWeight: 300, marginLeft: 4 }}>|</span>
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#444" }}>Campaign Planner</span>
        </div>

        {/* Step indicator */}
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          {(["brief", "arc-review", "lock-progress", "dashboard"] as PlannerStep[]).map((s, i) => {
            const labels: Record<PlannerStep, string> = {
              "brief":         "Brief",
              "arc-review":    "Arc",
              "lock-progress": "Lock",
              "dashboard":     "Dashboard",
            };
            const isActive  = step === s;
            const isPast    = (["brief", "arc-review", "lock-progress", "dashboard"] as PlannerStep[]).indexOf(s) <
                              (["brief", "arc-review", "lock-progress", "dashboard"] as PlannerStep[]).indexOf(step);
            return (
              <React.Fragment key={s}>
                {i > 0 && <span style={{ color: "#E4E4E0", fontSize: "12px" }}>›</span>}
                <span style={{
                  fontSize:   "12px",
                  fontWeight: isActive ? 700 : 500,
                  color:      isActive ? ACCENT : isPast ? "#2DB88F" : "#aaa",
                }}>
                  {labels[s]}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      </nav>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main style={mainStyle}>
        {step === "brief" && (
          <StepBrief
            templates={templates}
            templatesLoaded={templatesLoaded}
            onSubmit={handleCreateCampaign}
            isSubmitting={isSubmitting}
            submitError={submitError}
            runningCampaigns={runningCampaigns}
          />
        )}

        {step === "arc-review" && campaign && (
          <StepArcReview
            campaignId={campaign.id}
            campaignName={campaign.name}
            corePrinciple={campaign.corePrinciple}
            arc={campaign.storyArc}
            arcTemplate={campaign.arcTemplate}
            apiUrl={API_URL}
            authHeaders={getAuthHeaders()}
            onSaveArc={handleSaveArc}
            onArcRegenerated={handleArcRegenerated}
            onLock={() => setStep("lock-progress")}
            isSaving={isSavingArc}
            saveError={arcSaveError}
          />
        )}

        {step === "lock-progress" && campaign && (
          <StepLockProgress
            campaign={campaign}
            onLock={handleLock}
            onPollTick={fetchCampaign}
            onComplete={handleLockComplete}
            isLocking={isLocking}
            lockError={lockError}
          />
        )}

        {step === "dashboard" && campaign && (
          <StepDashboard
            campaign={campaign}
            onPollTick={fetchCampaign}
            onPause={handlePause}
            onResume={handleResume}
            onSkipPost={handleSkipPost}
            onReschedulePost={handleReschedulePost}
            onDelete={handleDelete}
            onReset={handleReset}
            authHeaders={getAuthHeaders()}
            apiUrl={API_URL}
          />
        )}
      </main>
    </div>
  );
};

export default CampaignPlannerPage;
